/**
 * สร้างไฟล์ report ของหน้า "สรุปการอนุมัติ" — ตรรกะล้วน ไม่พึ่ง React/DOM (ทดสอบได้)
 * ไฟล์เดียวมี 2 ส่วน:
 *   ส่วนบน  สรุปรายคน — ยอดรวมของช่วงที่ดู (ทบทวนก่อนปิดรอบเงินเดือน)
 *   ส่วนล่าง รายละเอียดรายวัน — 1 แถวต่อ 1 รายการ (ลา/OT/มาสาย/ขาดงาน) กรอง/pivot ใน Excel ได้
 */
import type { MonthlyApprovalSummary } from '../hooks';

/** ชนิดลา → หัวคอลัมน์ในสรุปรายคน (แยกสิทธิ์ที่ใช้ต่อคน) */
export const LEAVE_TYPE_COLS: { type: string; label: string }[] = [
  { type: 'sick', label: 'ลาป่วย' },
  { type: 'personal', label: 'ลากิจ' },
  { type: 'vacation', label: 'พักร้อน' },
  { type: 'unpaid', label: 'ลาไม่รับเงิน' },
  { type: 'maternity', label: 'ลาคลอด' },
];

/** ครอบค่าด้วย " เมื่อมี , " หรือขึ้นบรรทัดใหม่ (กติกา CSV — เหตุผลที่พนักงานพิมพ์มีจุลภาคได้) */
const esc = (v: string | number) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const round = (n: number) => Math.round(n * 1000) / 1000;
const timeRange = (from: string | null, to: string | null) => (from && to ? `${from.slice(0, 5)}–${to.slice(0, 5)}` : '');

/**
 * @param periodLabel ข้อความช่วงเวลาที่โชว์หัวไฟล์ เช่น "เมษายน 2569" / "ทั้งปี 2569"
 * @param focusEmp    ถ้าไม่ null = เอาเฉพาะคนนั้น (ให้ไฟล์ตรงกับที่กรองบนหน้าจอ)
 * @param position    ถ้าไม่ว่าง = เอาเฉพาะคนตำแหน่งนั้น (fb#492 ตัวกรองตำแหน่งบนหน้าจอ)
 * @returns CSV พร้อม BOM นำหน้า (กันภาษาไทยเพี้ยนตอนเปิดใน Excel)
 */
export function buildReportCsv(data: MonthlyApprovalSummary, periodLabel: string, focusEmp: string | null, position = ''): string {
  const posOf = new Map(data.by_emp.map((e) => [e.emp_code, e.position ?? '']));
  const keep = <T extends { emp_code: string }>(arr: T[]) =>
    arr.filter((x) => (!focusEmp || x.emp_code === focusEmp) && (!position || posOf.get(x.emp_code) === position));

  // ── ส่วนบน: สรุปรายคน
  // fb#492 — ตำแหน่ง/แผนก (TN เน้นตำแหน่งเป็นหลักในทุกรายงาน) · รายละเอียดรายวันหยิบจาก by_emp (ทุกคนในรายการอยู่ใน by_emp)
  const orgOf = new Map(data.by_emp.map((e) => [e.emp_code, [e.position ?? '', e.department ?? '']]));
  const org = (code: string) => orgOf.get(code) ?? ['', ''];
  const sumHeader = ['รหัส', 'ชื่อ', 'ตำแหน่ง', 'แผนก', 'ลารวม(วัน)', ...LEAVE_TYPE_COLS.map((c) => c.label), 'OT(ใบ)', 'OT(ชม.)', 'สาย(ครั้ง)', 'สาย-ยังไม่จัดการ', 'ขาด(ครั้ง)', 'ขาด-ยังไม่จัดการ'];
  const sumRows = keep(data.by_emp).map((e) => [
    e.emp_code, e.emp_name, ...org(e.emp_code), round(e.leave_days) || 0,
    ...LEAVE_TYPE_COLS.map((c) => round(e.leave_by_type?.[c.type] ?? 0) || 0),
    e.ot_count || 0, round(e.ot_hours ?? 0) || 0,
    e.late_count || 0, e.late_open || 0, e.absent_count || 0, e.absent_open || 0,
  ]);

  // ── ส่วนล่าง: รายละเอียดรายวัน (เรียงตามชนิดรายการ แล้วตามวันที่ — ลำดับเดียวกับบนหน้าจอ)
  const detailHeader = ['ประเภท', 'รหัส', 'ชื่อ', 'ตำแหน่ง', 'แผนก', 'วันที่', 'ถึงวันที่', 'จำนวนวัน', 'จำนวนชั่วโมง', 'ชนิด', 'เวลา', 'เหตุผล', 'สถานะ'];
  const detailRows: (string | number)[][] = [
    ...keep(data.leaves).map((l) => [
      'ลา', l.emp_code, l.emp_name, ...org(l.emp_code), l.date, l.end_date, round(l.days), '', l.type_label, '', l.reason ?? '', '',
    ]),
    ...keep(data.ots).map((o) => [
      'OT', o.emp_code, o.emp_name, ...org(o.emp_code), o.date, '', '', round(o.hours ?? 0), o.type_label, timeRange(o.start_time, o.end_time), o.reason ?? '', '',
    ]),
    ...keep(data.late).map((e) => [
      'มาสาย', e.emp_code, e.emp_name, ...org(e.emp_code), e.date, '', '', '', '', '', e.detail ?? '', e.resolved ? 'จัดการแล้ว' : 'ค้าง',
    ]),
    ...keep(data.absent).map((e) => [
      'ขาดงาน', e.emp_code, e.emp_name, ...org(e.emp_code), e.date, '', '', '', '', '', e.detail ?? '', e.resolved ? 'จัดการแล้ว' : 'ค้าง',
    ]),
  ];

  const line = (cells: (string | number)[]) => cells.map(esc).join(',');
  const out = [
    line([`สรุปรายคน (${periodLabel})`]),
    line([
      'คอลัมน์ "OT(ชม.)" = ชั่วโมงรวมจากใบ OT ที่อนุมัติ ปกติตรงกับที่ระบบสรุปไว้ตอนคิดเงิน — ' +
        'ยกเว้นค่าทำงานวันหยุดที่ระบบคิดให้เองจากการสแกนโดยไม่ต้องมีใบ OT (ฝั่งระบบจะมากกว่า) ' +
        'อยากวางเทียบเป็นคอลัมน์คู่ ให้ดึงไฟล์ "รายงานเวลาเข้า-ออก ลา และ OT" แบบงวดรายเดือน',
    ]),
    line([
      'หมายเหตุ: พนักงานโหมดเงินเดือนล้วนและพนักงานรายชั่วโมง จะมีชั่วโมง OT ขึ้นในไฟล์นี้ แต่ระบบไม่จ่ายเงิน OT ให้',
    ]),
    line(sumHeader),
    ...sumRows.map(line),
    '', // บรรทัดว่างคั่น 2 ส่วน
    line(['รายละเอียดรายวัน']),
    line(detailHeader),
    ...detailRows.map(line),
  ];
  return '﻿' + out.join('\n') + '\n';
}
